/**
 * Auth context — spec §5-F1, §8.1.
 *
 * On mount it tries one silent refresh so a returning user with a live refresh
 * cookie never sees the login screen. The access token is kept in memory only;
 * the refresh token lives in an httpOnly cookie the JS never touches.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Permission, SystemRole } from '@virtuetasks/types';
import { ApiError, api, setAccessToken, setAuthLostHandler } from '@/lib/api';

export interface AuthUser {
  _id: string;
  workspaceId: string;
  email: string;
  name: string;
  avatarUrl?: string;
  systemRole: SystemRole;
  designation: string;
  timezone: string;
  status: string;
  twoFactorEnabled: boolean;
  phoneVerified: boolean;
  preferences: {
    theme: 'light' | 'dark' | 'system';
    density: 'comfortable' | 'compact';
    defaultView: string;
    defaultProjectId?: string | null;
    quietHours: { enabled: boolean; start: string; end: string };
    notificationMatrix: Record<string, Record<string, boolean>>;
  };
  permissions: Permission[];
}

export interface AuthWorkspace {
  _id: string;
  name: string;
  slug: string;
  logoUrl?: string;
  timezone: string;
  workingDays: number[];
  workingHours: { start: string; end: string };
  holidays: { date: string; name: string }[];
  settings: Record<string, unknown>;
}

interface LoginArgs {
  email: string;
  password: string;
  totp?: string;
  recoveryCode?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  workspace: AuthWorkspace | null;
  permissions: Permission[];
  status: 'loading' | 'authenticated' | 'anonymous';
  login: (args: LoginArgs) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
  applySession: (accessToken: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface MeResponse {
  user: AuthUser;
  workspace: AuthWorkspace | null;
  permissions: Permission[];
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [workspace, setWorkspace] = useState<AuthWorkspace | null>(null);
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'anonymous'>('loading');
  const queryClient = useQueryClient();

  const loadMe = useCallback(async () => {
    const { data } = await api.get<MeResponse>('/auth/me');
    setUser(data.user);
    setWorkspace(data.workspace);
    setStatus('authenticated');
  }, []);

  const applySession = useCallback(
    async (accessToken: string) => {
      setAccessToken(accessToken);
      await loadMe();
    },
    [loadMe],
  );

  const login = useCallback(
    async (args: LoginArgs) => {
      const { data } = await api.post<{ accessToken: string; user: AuthUser }>(
        '/auth/login',
        args,
        { noRetry: true },
      );
      // Drop everything the previous session cached before the new one starts
      // reading. Only `logout` did this, so signing in as someone else without
      // signing out first left their data resident — most visibly the
      // `platform-access` probe, which kept the Platform link on screen for
      // five minutes under an account that has no such access.
      queryClient.clear();
      await applySession(data.accessToken);
    },
    [applySession, queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Signing out locally matters more than the server round trip succeeding.
    }
    setAccessToken(null);
    setUser(null);
    setWorkspace(null);
    setStatus('anonymous');
    queryClient.clear();
  }, [queryClient]);

  // One silent refresh at startup; failure just means "show the login page".
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await api.post<{ accessToken: string }>('/auth/refresh', undefined, {
          noRetry: true,
        });
        if (cancelled) return;
        await applySession(data.accessToken);
      } catch (err) {
        if (cancelled) return;
        if (!(err instanceof ApiError)) {
          // Network failure rather than a rejected session — still anonymous,
          // but worth surfacing in the console for debugging.
          console.warn('session restore failed', err);
        }
        setAccessToken(null);
        setStatus('anonymous');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applySession]);

  // A 401 that survives the retry means the session is gone for good.
  useEffect(() => {
    setAuthLostHandler(() => {
      setAccessToken(null);
      setUser(null);
      setWorkspace(null);
      setStatus('anonymous');
    });
    return () => setAuthLostHandler(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      workspace,
      permissions: user?.permissions ?? [],
      status,
      login,
      logout,
      refreshMe: loadMe,
      applySession,
    }),
    [user, workspace, status, login, logout, loadMe, applySession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/**
 * Hides UI a user cannot use. This is UX, not security — the server always
 * re-checks (§3.4).
 */
export function useCan(): (permission: Permission) => boolean {
  const { permissions } = useAuth();
  return useCallback(
    (permission: Permission) => permissions.includes(permission),
    [permissions],
  );
}
